import React, { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, UserCircle, SignOut, List, X } from '@phosphor-icons/react';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Header = () => {
  const { user, login, register, logout, cartCount } = useContext(AuthContext);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleAuth = async (e) => {
    e.preventDefault();
    let success = false;
    if (authMode === 'login') {
      success = await login(formData.email, formData.password);
    } else {
      success = await register(formData.email, formData.password, formData.name);
    }
    if (success) {
      setShowAuthDialog(false);
      setFormData({ email: '', password: '', name: '' });
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-white/10">
      <div className="container mx-auto px-6 py-3 flex items-center justify-between">
        {/* Premium Logo */}
        <Link to="/" className="flex items-center space-x-3 group" data-testid="header-logo-link">
          <img 
            src="https://customer-assets.emergentagent.com/job_fb7df63c-69aa-43b5-bde9-d9ace2d046d7/artifacts/83jahsha_file_000000001dd071fa989fc5a446c0d041.png" 
            alt="TrueDroplet Water" 
            className="h-11 w-auto transition-transform duration-300 group-hover:scale-105"
          />
          <div className="hidden sm:block">
            <span className="block text-lg font-heading font-semibold tracking-wide text-foreground leading-none">TRUEDROPLET</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] leading-tight mt-0.5">Pure Water, Your Label</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className={`text-xs uppercase tracking-[0.15em] transition-colors ${isActive('/') ? 'text-[#D4AF37]' : 'text-slate-300 hover:text-[#D4AF37]'}`} data-testid="nav-home-link">
            Home
          </Link>
          <Link to="/products" className={`text-xs uppercase tracking-[0.15em] transition-colors ${isActive('/products') ? 'text-[#D4AF37]' : 'text-slate-300 hover:text-[#D4AF37]'}`} data-testid="nav-products-link">
            Products
          </Link>
          <Link to="/contact" className={`text-xs uppercase tracking-[0.15em] transition-colors ${isActive('/contact') ? 'text-[#D4AF37]' : 'text-slate-300 hover:text-[#D4AF37]'}`} data-testid="nav-contact-link">
            Contact
          </Link>
          {user && (
            <Link to="/orders" className={`text-xs uppercase tracking-[0.15em] transition-colors ${isActive('/orders') ? 'text-primary' : 'text-slate-300 hover:text-primary'}`} data-testid="nav-orders-link">
              Orders
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link to="/admin" className={`text-xs uppercase tracking-[0.15em] transition-colors ${isActive('/admin') ? 'text-primary' : 'text-slate-300 hover:text-primary'}`} data-testid="nav-admin-link">
              Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center space-x-4">
          {user ? (
            <>
              <Link to="/cart" className="relative" data-testid="cart-button">
                <ShoppingCart size={22} weight="regular" className="text-slate-300 hover:text-primary transition-colors" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
              <div className="relative group">
                <button className="text-slate-300 hover:text-primary transition-colors" data-testid="user-menu-button">
                  <UserCircle size={22} weight="regular" />
                </button>
                <div className="absolute right-0 mt-2 w-48 glass-effect rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="p-4 space-y-2">
                    <p className="text-xs text-slate-400">{user.email}</p>
                    <button onClick={logout} className="flex items-center space-x-2 text-sm text-slate-300 hover:text-primary transition-colors w-full" data-testid="logout-button">
                      <SignOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-sm text-xs uppercase tracking-wider" data-testid="login-button">
                  <User size={16} weight="regular" className="mr-1.5" />
                  Sign In
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border" aria-describedby="auth-description" data-testid="auth-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Welcome to TrueDroplet</DialogTitle>
                  <p id="auth-description" className="text-sm text-slate-400">Sign in or create an account to start customizing your premium PET water bottles.</p>
                </DialogHeader>
                <Tabs value={authMode} onValueChange={setAuthMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
                    <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                    <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                  </TabsList>
                  <TabsContent value="login">
                    <form onSubmit={handleAuth} className="space-y-4" data-testid="login-form">
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="login-email-input" />
                      </div>
                      <div>
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="login-password-input" />
                      </div>
                      <Button type="submit" className="w-full rounded-sm" data-testid="login-submit-button">Login</Button>
                    </form>
                  </TabsContent>
                  <TabsContent value="register">
                    <form onSubmit={handleAuth} className="space-y-4" data-testid="register-form">
                      <div>
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="register-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="reg-email">Email</Label>
                        <Input id="reg-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="register-email-input" />
                      </div>
                      <div>
                        <Label htmlFor="reg-password">Password</Label>
                        <Input id="reg-password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="register-password-input" />
                      </div>
                      <Button type="submit" className="w-full rounded-sm" data-testid="register-submit-button">Create Account</Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          )}

          {/* Mobile menu toggle */}
          <button className="md:hidden text-slate-300" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass-effect border-t border-white/5 px-6 py-4 space-y-3" data-testid="mobile-menu">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-300 hover:text-primary">Home</Link>
          <Link to="/products" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-300 hover:text-primary">Products</Link>
          <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-300 hover:text-primary">Contact</Link>
          {user && <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-300 hover:text-primary">Orders</Link>}
          {user?.role === 'admin' && <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-300 hover:text-primary">Admin</Link>}
        </div>
      )}
    </header>
  );
};

export default Header;
